import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { ArrowLeft, Plus, Calendar, MapPin, Trash2, X, Pencil, Lock, Unlock } from "lucide-react";
import { format, parseISO } from "date-fns";

const EVENT_TYPES = ["wedding", "mehendi", "sangeet", "haldi", "reception", "engagement", "trial", "meeting"];
const SLOT_OPTIONS = ["Morning", "Afternoon", "Evening", "Others"];
function eventTypeLabel(t) { return t.charAt(0).toUpperCase() + t.slice(1); }
function formatDate(d) { if (!d) return "—"; try { return format(parseISO(d), "EEE, dd MMM yyyy"); } catch { return d; } }
function formatDateRange(start, end) {
  if (!start && !end) return "—";
  if (!end || start === end) return formatDate(start);
  try {
    const s = parseISO(start); const e = parseISO(end);
    if (s.getFullYear() === e.getFullYear()) return `${format(s, "dd MMM")} – ${format(e, "dd MMM yyyy")}`;
    return `${formatDate(start)} – ${formatDate(end)}`;
  } catch { return `${start} – ${end}`; }
}
function INR(n, currency = "INR") { try { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n || 0); } catch { return `${currency} ${n}`; } }

function AdvanceCard({ wedding, onSaved }) {
  const isConfirmed = wedding.status === "confirmed";
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(String(wedding.advance_amount || ""));
  const [date, setDate] = useState(wedding.advance_date || new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const total = wedding.total_price || 0;
  const adv = wedding.advance_amount || 0;
  const balance = Math.max(total - adv, 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/weddings/${wedding.id}`, { advance_amount: Number(amount) || 0, advance_date: date });
      toast.success("Advance recorded");
      setEditing(false);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 bg-white border border-[#EAE7E0]" data-testid="advance-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652]">Advance received</div>
        {isConfirmed && !editing && (
          <button onClick={() => setEditing(true)} data-testid="edit-advance-button" className="text-[10px] uppercase tracking-[0.2em] text-[#C88270] hover:text-[#B37363]">
            {adv ? "Edit" : "Record"}
          </button>
        )}
      </div>
      {!isConfirmed ? (
        <div className="text-sm text-[#B8B2AA]" data-testid="advance-locked-hint">Confirm this wedding to record an advance.</div>
      ) : editing ? (
        <div className="space-y-3">
          <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Advance amount" data-testid="advance-amount-input"
            className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="advance-date-input"
            className="w-full border-b border-[#EAE7E0] bg-transparent py-2 text-sm focus:border-[#C88270] focus:outline-none" />
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} data-testid="save-advance-button" className="px-4 py-1.5 bg-[#C88270] text-white text-[10px] uppercase tracking-[0.2em] hover:bg-[#B37363] disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
            <button onClick={() => { setEditing(false); setAmount(String(wedding.advance_amount || "")); }} className="px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#5C5652]">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="font-serif text-3xl tracking-tight text-[#2A2421]">{INR(adv, wedding.currency)}</div>
          <div className="mt-2 text-xs text-[#5C5652]">
            {wedding.advance_date ? `Received ${wedding.advance_date} · ` : ""}Balance {INR(balance, wedding.currency)}
          </div>
        </>
      )}
    </div>
  );
}




function SubEventFormModal({ open, onClose, onSaved, users, weddingId, coupleNames }) {
  const initial = { title: "", event_type: "mehendi", event_date: "", ready_by: "", slots: [], location: "", notes: "", total_price: 0, currency: "INR", assigned: [] };
  const [form, setForm] = useState(initial);
  const [assignSelect, setAssignSelect] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial); /* eslint-disable-next-line */ }, [open]);
  if (!open) return null;

  const addAssignment = () => {
    if (!assignSelect) return;
    const u = users.find((x) => x.id === assignSelect);
    if (!u || form.assigned.some((a) => a.team_member_id === u.id)) return;
    setForm({ ...form, assigned: [...form.assigned, { team_member_id: u.id, service_type: u.service_types?.[0] || "makeup", agreed_amount: 0 }] });
    setAssignSelect("");
  };
  const removeAssignment = (id) => setForm({ ...form, assigned: form.assigned.filter((a) => a.team_member_id !== id) });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        wedding_id: weddingId,
        couple_names: coupleNames,
        total_price: Number(form.total_price) || 0,
        assigned: form.assigned.map((a) => ({ ...a, agreed_amount: Number(a.agreed_amount) || 0 })),
        title: form.title || `${eventTypeLabel(form.event_type)} — ${coupleNames}`,
      };
      await api.post("/events", payload);
      toast.success("Sub-event added");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="subevent-form-modal">
      <div className="bg-white border border-[#EAE7E0] max-w-2xl w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#EAE7E0]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652]">Add sub-event</div>
            <h2 className="font-serif text-3xl tracking-tight">{coupleNames}</h2>
          </div>
          <button onClick={onClose} className="text-[#5C5652] hover:text-[#2A2421]" data-testid="close-subevent"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Event type *</label>
              <input list="subevent-type-options" required value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="subevent-type-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" placeholder="mehendi, cocktail, pool-party…" />
              <datalist id="subevent-type-options">
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{eventTypeLabel(t)}</option>)}
              </datalist>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Date *</label>
              <input required type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="subevent-date-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Title (optional)</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="subevent-title-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" placeholder="Auto: Mehendi — Sarah & James" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Sub-price</label>
              <input type="number" min="0" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} data-testid="subevent-price-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Ready by</label>
              <input type="time" value={form.ready_by} onChange={(e) => setForm({ ...form, ready_by: e.target.value })} data-testid="subevent-readyby-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Location</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="subevent-location-input"
              className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-3">Slot to be blocked</div>
            <div className="flex flex-wrap gap-2" data-testid="subevent-slots">
              {SLOT_OPTIONS.map((s) => (
                <button type="button" key={s} data-testid={`subevent-slot-${s.toLowerCase()}`}
                  onClick={() => setForm({ ...form, slots: form.slots.includes(s) ? [] : [s] })}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${form.slots.includes(s) ? "bg-[#C88270] text-white border-[#C88270]" : "border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421]"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-3">Team for this event</div>
            <div className="flex gap-2 mb-3">
              <select value={assignSelect} onChange={(e) => setAssignSelect(e.target.value)} data-testid="subevent-assign-select"
                className="flex-1 border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none">
                <option value="">Select team member…</option>
                {users.filter((u) => u.role !== "admin").map((u) => (
                  <option key={u.id} value={u.id}>{u.name} — {u.service_types?.join(", ") || "team"}</option>
                ))}
              </select>
              <button type="button" onClick={addAssignment} data-testid="subevent-assign-add"
                className="px-4 py-2 border border-[#2A2421] text-[#2A2421] text-xs uppercase tracking-[0.2em] hover:bg-[#2A2421] hover:text-white transition-colors">Add</button>
            </div>
            {form.assigned.map((a) => {
              const u = users.find((x) => x.id === a.team_member_id);
              return (
                <div key={a.team_member_id} className="flex items-center gap-3 py-2 border-b border-[#EAE7E0]">
                  <div className="flex-1 text-sm">{u?.name}</div>
                  <input value={a.service_type} onChange={(e) => setForm({ ...form, assigned: form.assigned.map((x) => x.team_member_id === a.team_member_id ? { ...x, service_type: e.target.value } : x) })}
                    className="w-40 border-b border-[#EAE7E0] py-1 text-sm focus:border-[#C88270] focus:outline-none" />
                  <button type="button" onClick={() => removeAssignment(a.team_member_id)} className="text-[#5C5652] hover:text-red-600"><X size={16} /></button>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-[#EAE7E0]">
            <button type="button" onClick={onClose} className="px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#5C5652]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="subevent-submit-button"
              className="px-6 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Add sub-event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function WeddingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const confirm = useConfirm();
  const [wedding, setWedding] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [w, u] = await Promise.all([api.get(`/weddings/${id}`), api.get("/users")]);
      setWedding(w.data);
      setUsers(u.data);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/weddings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onDelete = async () => {
    if (!(await confirm({ title: "Delete wedding?", description: "This permanently deletes the wedding and ALL its sub-events and expenses.", confirmText: "Delete" }))) return;
    try { await api.delete(`/weddings/${id}`); toast.success("Wedding deleted"); navigate("/weddings"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleWeddingLock = async () => {
    const next = !wedding.expenses_locked;
    if (!(await confirm({ title: next ? "Close expenses for this wedding?" : "Reopen expenses?", description: next ? "This stops team members from adding or submitting expenses for this wedding AND all its sub-events." : "Team members will be able to submit expenses again for this wedding and its sub-events.", confirmText: next ? "Close all" : "Reopen" }))) return;
    try { await api.post(`/weddings/${id}/expenses-lock`, { locked: next }); toast.success(next ? "Expenses closed for the whole wedding" : "Expenses reopened"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };


  const onDeleteSubEvent = async (evId) => {
    if (!(await confirm({ title: "Delete sub-event?", description: "This permanently deletes this sub-event and its expenses.", confirmText: "Delete" }))) return;
    try { await api.delete(`/events/${evId}`); toast.success("Sub-event deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = () => {
    setEditForm({
      couple_names: wedding.couple_names || "",
      start_date: wedding.start_date || wedding.primary_date || "",
      end_date: wedding.end_date || "",
      location: wedding.location || "",
      total_price: wedding.total_price || 0,
      contact_phone: wedding.contact_phone || "",
      instagram_id: wedding.instagram_id || "",
      notes: wedding.notes || "",
    });
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editForm, total_price: Number(editForm.total_price) || 0 };
      if (!payload.end_date) payload.end_date = payload.start_date;
      await api.patch(`/weddings/${id}`, payload);
      toast.success("Wedding updated");
      setEditing(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading || !wedding) return <div className="p-12 text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="wedding-detail-page">
      <Link to="/weddings" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C5652] hover:text-[#C88270] mb-8" data-testid="back-to-weddings">
        <ArrowLeft size={14} /> Back to weddings
      </Link>

      <div className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#C88270]">Wedding</div>
          <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.25em] border rounded-full ${
            wedding.status === "confirmed" ? "bg-[#4E9A6B]/12 text-[#357350] border-[#4E9A6B]/40"
            : wedding.status === "soft_block" ? "bg-[#E4B62C]/20 text-[#7a5f0c] border-[#E4B62C]/55"
            : wedding.status === "lost" ? "bg-red-50 text-red-700 border-red-200"
            : "bg-[#D4A373]/10 text-[#D4A373] border-[#D4A373]/30"
          }`} data-testid="wedding-status-badge">{wedding.status === "soft_block" ? "Soft Block" : (wedding.status || "pending")}</span>
          {isAdmin && (
            <select value={wedding.status || "pending"}
              onChange={async (e) => { try { await api.patch(`/weddings/${id}`, { status: e.target.value }); toast.success("Status updated"); load(); } catch (err) { toast.error(formatApiError(err)); } }}
              data-testid="wedding-status-change"
              className="text-[10px] uppercase tracking-[0.2em] border border-[#EAE7E0] bg-white px-2 py-1">
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="soft_block">Soft Block</option>
              <option value="lost">Lost</option>
            </select>
          )}
        </div>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight text-[#2A2421] leading-none">{wedding.couple_names}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        <div className="p-8 bg-white border border-[#EAE7E0]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Dates</div>
          <div className="font-serif text-2xl tracking-tight text-[#2A2421]">{formatDateRange(wedding.start_date || wedding.primary_date, wedding.end_date)}</div>
        </div>
        <div className="p-8 bg-white border border-[#EAE7E0]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Primary location</div>
          <div className="text-sm text-[#2A2421]">{wedding.location || "—"}</div>
        </div>
        {isAdmin && (
          <div className="p-8 bg-white border border-[#EAE7E0]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Total price</div>
            <div className="font-serif text-3xl tracking-tight text-[#2A2421]">{INR(wedding.total_price, wedding.currency)}</div>
          </div>
        )}
        {isAdmin && <AdvanceCard wedding={wedding} onSaved={load} />}
      </div>

      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-serif text-3xl tracking-tight">Sub-events</h2>
        {isAdmin && wedding.status !== "confirmed" && wedding.status !== "soft_block" && (
          <div className="mb-4 p-4 border border-[#D4A373]/40 bg-[#D4A373]/10 text-sm text-[#5C5652]" data-testid="pending-lead-hint">
            Confirm or soft-block this lead to schedule sub-events. Use the status dropdown at the top of the page.
          </div>
        )}
        {isAdmin && (wedding.status === "confirmed" || wedding.status === "soft_block") && (
          <button onClick={() => setModal(true)} data-testid="add-subevent-button"
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors">
            <Plus size={14} /> Add sub-event
          </button>
        )}
      </div>

      {wedding.events?.length === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652] mb-16">
          {isAdmin ? "No sub-events yet — add Mehendi, Sangeet, Reception, etc." : "You are not yet assigned to any sub-event."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {wedding.events.map((ev) => (
            <Link key={ev.id} to={`/events/${ev.id}`} data-testid={`subevent-card-${ev.id}`}
              className="relative p-6 bg-white border border-[#EAE7E0] hover:border-[#C88270]/60 transition-colors group">
              {isAdmin && (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDeleteSubEvent(ev.id); }}
                  data-testid={`delete-subevent-${ev.id}`}
                  className="absolute top-4 right-4 text-[#5C5652] hover:text-red-700 z-10" title="Delete sub-event">
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              )}
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C88270] mb-3">{eventTypeLabel(ev.event_type)}</div>
              <div className="font-serif text-2xl tracking-tight text-[#2A2421] group-hover:text-[#C88270] transition-colors">{ev.title}</div>
              <div className="space-y-2 text-xs text-[#5C5652] mt-6 border-t border-[#EAE7E0] pt-4">
                <div className="flex items-center gap-2"><Calendar size={14} strokeWidth={1.5} /> {formatDate(ev.event_date)} {ev.ready_by && `· ready by ${ev.ready_by}`}</div>
                {ev.slots?.length > 0 && <div className="text-[#C88270]">Slots: {ev.slots.join(", ")}</div>}
                {ev.location && <div className="flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} /> {ev.location}</div>}
                <div className="pt-1">{ev.assigned?.length || 0} team member{ev.assigned?.length === 1 ? "" : "s"}</div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {wedding.notes && (
        <div className="mb-16">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-4">Notes</div>
          <div className="p-8 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] whitespace-pre-wrap">{wedding.notes}</div>
        </div>
      )}

      {isAdmin && (
        <div className="pt-8 border-t border-[#EAE7E0] flex gap-6">
          <button onClick={openEdit} data-testid="edit-wedding-button"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#2A2421] hover:text-[#C88270]">
            <Pencil size={14} /> Edit wedding
          </button>
          <button onClick={onDelete} data-testid="delete-wedding-button"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-red-700 hover:text-red-900">
            <Trash2 size={14} /> Delete wedding
          </button>
          <button onClick={toggleWeddingLock} data-testid="toggle-wedding-lock"
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] ${wedding.expenses_locked ? "text-[#829384] hover:text-[#6d7c6f]" : "text-[#2A2421] hover:text-[#C88270]"}`}>
            {wedding.expenses_locked ? <><Unlock size={14} /> Reopen expenses</> : <><Lock size={14} /> Close all expenses</>}
          </button>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="edit-wedding-modal">
          <div className="bg-white border border-[#EAE7E0] max-w-lg w-full my-8">
            <div className="flex items-center justify-between p-6 border-b border-[#EAE7E0]">
              <h2 className="font-serif text-3xl tracking-tight">Edit Wedding</h2>
              <button onClick={() => setEditing(false)} className="text-[#5C5652] hover:text-[#2A2421]"><X size={20} /></button>
            </div>
            <form onSubmit={saveEdit} className="p-6 space-y-4">
              {[
                { k: "couple_names", label: "Couple names *", required: true },
                { k: "location", label: "Primary location" },
                { k: "contact_phone", label: "Contact phone" },
                { k: "instagram_id", label: "Instagram ID" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">{f.label}</label>
                  <input required={f.required} value={editForm[f.k] || ""} onChange={(e) => setEditForm({ ...editForm, [f.k]: e.target.value })}
                    data-testid={`edit-${f.k}`}
                    className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                </div>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Start date</label>
                  <input type="date" value={editForm.start_date || ""} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} data-testid="edit-start-date" className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">End date</label>
                  <input type="date" min={editForm.start_date || undefined} value={editForm.end_date || ""} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} data-testid="edit-end-date" className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Total (INR)</label>
                <input type="number" min="0" value={editForm.total_price || 0} onChange={(e) => setEditForm({ ...editForm, total_price: e.target.value })} data-testid="edit-total-price" className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Notes</label>
                <textarea rows={3} value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-notes" className="w-full border border-[#EAE7E0] bg-transparent p-3 focus:border-[#C88270] focus:outline-none" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-[#EAE7E0]">
                <button type="button" onClick={() => setEditing(false)} className="px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#5C5652]">Cancel</button>
                <button type="submit" data-testid="edit-wedding-submit" className="px-6 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <SubEventFormModal open={modal} onClose={() => setModal(false)} onSaved={load} users={users} weddingId={id} coupleNames={wedding.couple_names} />
    </div>
  );
}
